import React, { useState } from 'react';
import adminService from '../services/adminService';
import { ArrowLeft, ArrowUp, ArrowDown } from 'lucide-react';

const ClusterCustomerModal = ({ isOpen, cluster, customers, loading, onClose, formatCurrency, selectedMonth }) => {
    const [view, setView] = useState('customers'); // 'customers' | 'invoices' | 'items'
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [loadingItems, setLoadingItems] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'sales', direction: 'desc' });
    const [searchTerm, setSearchTerm] = useState('');

    // Filter customers
    const getFilteredCustomers = () => {
        if (!customers) return [];
        if (!searchTerm) return customers;

        const term = searchTerm.toLowerCase();
        return customers.filter(c =>
            c.name.toLowerCase().includes(term) ||
            c.identification.includes(term) ||
            (c.invoice_numbers && c.invoice_numbers.toLowerCase().includes(term))
        );
    };

    // Handle sort
    const handleSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedCustomers = () => {
        const filtered = getFilteredCustomers();
        if (filtered.length === 0) return [];
        const sorted = [...filtered];
        sorted.sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
        return sorted;
    };

    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return <ArrowDown size={14} className="ml-1 text-gray-300 opacity-0 group-hover:opacity-50" />;
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={14} className="ml-1 text-blue-600" />
            : <ArrowDown size={14} className="ml-1 text-blue-600" />;
    };

    // Reset view when modal closes or opens
    React.useEffect(() => {
        if (!isOpen) {
            setView('customers');
            setSelectedCustomer(null);
            setSelectedInvoice(null);
            setInvoices([]);
            setInvoiceItems([]);
        }
    }, [isOpen]);

    const handleCustomerClick = async (customer) => {
        setSelectedCustomer(customer);
        setLoadingInvoices(true);
        setView('invoices');
        try {
            // Calculate date range from selectedMonth (YYYY-MM)
            const [year, month] = selectedMonth.split('-');
            const startDate = `${year}-${month}-01`;

            const now = new Date();
            const selectedMonthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
            const isCurrentMonth = now.getFullYear() === selectedMonthDate.getFullYear() &&
                now.getMonth() === selectedMonthDate.getMonth();

            let endDate;
            if (isCurrentMonth) {
                endDate = now.toISOString().slice(0, 10) + ' 23:59:59';
            } else {
                const lastDayOfMonth = new Date(parseInt(year), parseInt(month), 0);
                endDate = lastDayOfMonth.toISOString().slice(0, 10) + ' 23:59:59';
            }

            const response = await adminService.getCustomerInvoicesProfitability(customer.id, { startDate, endDate });

            if (response.success) {
                setInvoices(response.data);
            }
        } catch (error) {
            console.error('Error loading invoices', error);
        } finally {
            setLoadingInvoices(false);
        }
    };

    const handleInvoiceClick = async (invoice) => {
        setSelectedInvoice(invoice);
        setLoadingItems(true);
        setView('items');
        try {
            const response = await adminService.getInvoiceItemsProfitability(invoice.id);
            if (response.success) {
                setInvoiceItems(response.data);
            }
        } catch (error) {
            console.error('Error loading invoice items', error);
        } finally {
            setLoadingItems(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        {view !== 'customers' && (
                            <button
                                onClick={() => {
                                    if (view === 'items') setView('invoices');
                                    else setView('customers');
                                }}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <ArrowLeft size={20} className="text-gray-600" />
                            </button>
                        )}
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">
                                {view === 'customers'
                                    ? `Clientes ${cluster ? `- ${cluster.charAt(0).toUpperCase() + cluster.slice(1)}` : ''}`
                                    : view === 'invoices'
                                        ? `Facturas: ${selectedCustomer?.name}`
                                        : `Items: ${selectedInvoice?.order_number}`
                                }
                            </h2>
                            {view === 'invoices' && selectedCustomer && (
                                <p className="text-sm text-gray-500 mt-1">
                                    Rentabilidad Total: <span className="font-bold text-green-600">{formatCurrency(selectedCustomer.profit)}</span> ({selectedCustomer.margin.toFixed(1)}%)
                                </p>
                            )}
                            {view === 'items' && selectedInvoice && (
                                <p className="text-sm text-gray-500 mt-1">
                                    Total Factura: <span className="font-bold text-gray-800">{formatCurrency(selectedInvoice.sales)}</span> |
                                    Rentabilidad: <span className="font-bold text-green-600">{formatCurrency(selectedInvoice.profit)}</span> ({selectedInvoice.margin}%)
                                </p>
                            )}
                        </div>
                    </div>

                    {/* SEARCH BAR */}
                    {view === 'customers' && (
                        <div className="mx-4 flex-1 max-w-md">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Buscar cliente, NIT o # Factura..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>
                    )}

                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-6">
                    {view === 'customers' ? (
                        loading ? (
                            <div className="text-center py-8">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                <p className="mt-2 text-gray-600">Cargando clientes...</p>
                            </div>
                        ) : customers.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                No se encontraron clientes en este cluster
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr className="border-b-2 border-gray-200">
                                        <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">Cliente</th>
                                        <th className="py-3 px-4 text-center text-sm font-semibold text-gray-700">Ciudad</th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-gray-700">Pedidos</th>
                                        <th
                                            className="py-3 px-4 text-right text-sm font-semibold text-gray-700 cursor-pointer group hover:bg-gray-100 transition-colors"
                                            onClick={() => handleSort('sales')}
                                        >
                                            <div className="flex items-center justify-end">
                                                Ventas
                                                <SortIcon columnKey="sales" />
                                            </div>
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-sm font-semibold text-gray-700 cursor-pointer group hover:bg-gray-100 transition-colors"
                                            onClick={() => handleSort('profit')}
                                        >
                                            <div className="flex items-center justify-end">
                                                Rentabilidad
                                                <SortIcon columnKey="profit" />
                                            </div>
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-sm font-semibold text-gray-700 cursor-pointer group hover:bg-gray-100 transition-colors"
                                            onClick={() => handleSort('margin')}
                                        >
                                            <div className="flex items-center justify-end">
                                                Margen %
                                                <SortIcon columnKey="margin" />
                                            </div>
                                        </th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-gray-700">Ticket Prom.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getSortedCustomers().map((customer, idx) => (
                                        <tr
                                            key={idx}
                                            className="border-b hover:bg-blue-50 cursor-pointer transition-colors"
                                            onClick={() => handleCustomerClick(customer)}
                                        >
                                            <td className="py-3 px-4">
                                                <p className="font-medium text-gray-800 underline decoration-blue-300 decoration-2 underline-offset-2">{customer.name}</p>
                                                <p className="text-xs text-gray-500">{customer.identification}</p>
                                                {/* Mostrar facturas siempre (truncado) */}
                                                {customer.invoice_numbers && (
                                                    <p className="text-[10px] text-gray-400 mt-1 truncate max-w-[200px]" title={customer.invoice_numbers}>
                                                        {customer.invoice_numbers}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-center text-gray-600 text-sm">{customer.city || '-'}</td>
                                            <td className="py-3 px-4 text-right text-gray-700 font-medium">{customer.orders}</td>
                                            <td className="py-3 px-4 text-right font-semibold text-gray-800">{formatCurrency(customer.sales)}</td>
                                            <td className={`py-3 px-4 text-right font-bold ${customer.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {formatCurrency(customer.profit)}
                                            </td>
                                            <td className="py-3 px-4 text-right text-gray-700">
                                                {customer.margin.toFixed(1)}%
                                            </td>
                                            <td className="py-3 px-4 text-right text-gray-700">{formatCurrency(customer.avgTicket)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )
                    ) : view === 'invoices' ? (
                        // INVOICES VIEW
                        loadingInvoices ? (
                            <div className="text-center py-8">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                                <p className="mt-2 text-gray-600">Cargando facturas...</p>
                            </div>
                        ) : invoices.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                No se encontraron facturas para este cliente en el periodo seleccionado.
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-purple-50 sticky top-0">
                                    <tr className="border-b-2 border-purple-200">
                                        <th className="py-3 px-4 text-left text-sm font-semibold text-purple-900"># Pedido</th>
                                        <th className="py-3 px-4 text-left text-sm font-semibold text-purple-900">Factura SIIGO</th>
                                        <th className="py-3 px-4 text-center text-sm font-semibold text-purple-900">Fecha</th>
                                        <th className="py-3 px-4 text-center text-sm font-semibold text-purple-900">Estado</th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-purple-900">Venta Neta</th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-purple-900">Utilidad</th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-purple-900">% Margen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((inv, idx) => (
                                        <tr
                                            key={idx}
                                            className="border-b hover:bg-purple-50 cursor-pointer transition-colors"
                                            onClick={() => handleInvoiceClick(inv)}
                                        >
                                            <td className="py-3 px-4 font-medium text-gray-800 underline decoration-purple-300 decoration-2 underline-offset-2">{inv.order_number}</td>
                                            <td className="py-3 px-4 text-gray-600">{inv.siigo_invoice_number || '-'}</td>
                                            <td className="py-3 px-4 text-center text-gray-600">
                                                {new Date(inv.date).toLocaleDateString()}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${inv.status === 'entregado' ? 'bg-green-100 text-green-700' :
                                                    inv.status === 'cancelado' ? 'bg-red-100 text-red-700' :
                                                        'bg-yellow-100 text-yellow-700'
                                                    }`}>
                                                    {inv.status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-right font-semibold text-gray-800">{formatCurrency(inv.sales)}</td>
                                            <td className={`py-3 px-4 text-right font-bold ${inv.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {formatCurrency(inv.profit)}
                                            </td>
                                            <td className="py-3 px-4 text-right text-gray-700 font-bold">
                                                {inv.margin}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )
                    ) : (
                        // ITEMS VIEW
                        loadingItems ? (
                            <div className="text-center py-8">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                                <p className="mt-2 text-gray-600">Cargando productos...</p>
                            </div>
                        ) : invoiceItems.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                No hay items en esta factura.
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-orange-50 sticky top-0">
                                    <tr className="border-b-2 border-orange-200">
                                        <th className="py-3 px-4 text-left text-sm font-semibold text-orange-900">Producto</th>
                                        <th className="py-3 px-4 text-center text-sm font-semibold text-orange-900">Cant.</th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-orange-900">Costo Unit.</th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-orange-900">Precio Unit.</th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-orange-900">Desc. %</th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-orange-900">Venta Total</th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-orange-900">Utilidad</th>
                                        <th className="py-3 px-4 text-right text-sm font-semibold text-orange-900">% Margen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceItems.map((item, idx) => (
                                        <tr key={idx} className="border-b hover:bg-gray-50">
                                            <td className="py-3 px-4 font-medium text-gray-800">{item.product_name}</td>
                                            <td className="py-3 px-4 text-center text-gray-600">{item.quantity}</td>
                                            <td className="py-3 px-4 text-right text-gray-500 font-mono text-xs">{formatCurrency(item.unit_cost)}</td>
                                            <td className="py-3 px-4 text-right text-gray-600">{formatCurrency(item.unit_price)}</td>
                                            <td className="py-3 px-4 text-right text-gray-600">{item.discount_percent > 0 ? `${item.discount_percent}%` : '-'}</td>
                                            <td className="py-3 px-4 text-right font-semibold text-gray-800">{formatCurrency(item.sales)}</td>
                                            <td className={`py-3 px-4 text-right font-bold ${item.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {formatCurrency(item.profit)}
                                            </td>
                                            <td className="py-3 px-4 text-right text-gray-700">
                                                {item.margin}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )
                    )}
                </div>

                <div className="p-4 border-t border-gray-200 bg-gray-50 text-right text-sm text-gray-600">
                    {view === 'customers'
                        ? `Total: ${customers.length} clientes`
                        : view === 'invoices'
                            ? `Total: ${invoices.length} facturas`
                            : `Total: ${invoiceItems.length} items`
                    }
                </div>
            </div>
        </div>
    );
};

export default ClusterCustomerModal;
